import {
  IconAddressBook,
  IconChartBar,
  IconClockHour4,
  IconDatabase,
  IconFolders,
  IconHistory,
  IconLayoutDashboard,
  IconReportAnalytics,
  type Icon,
} from '@tabler/icons-react';

export interface ReportLink {
  /** Kod raportu z tabeli `reports` — na nim wisi uprawnienie `<kod>.read`. */
  code: string;
  to: string;
  label: string;
  icon: Icon;
}

export interface ReportGroup {
  title: string;
  icon: Icon;
  reports: ReportLink[];
}

// Jedyny spis raportów w apce. Menu boczne i kafle na pulpicie czytają stąd —
// wcześniej były to dwie niezależne listy i regularnie się rozjeżdżały (pulpit
// długo nie znał CRM-u ani historii dystrybucji, a RZiS-u nie znał do dziś).
// Kto dodaje raport, dopisuje jeden wiersz tutaj i tyle.
//
// Grupa to tylko szuflada w nawigacji — nie ma odbicia ani w uprawnieniach, ani
// w bazie. Uprawnienia wiszą na `code`, więc raport można przenieść między
// grupami bez ruszania czegokolwiek po stronie backendu.
//
// W etykietach nie używamy myślników ani półpauz: część z nich trafia do miejsc,
// gdzie dash jest nieczytelny albo mylący (nagłówki eksportów, treści alertów).
// Uzupełnienie nazwy idzie w nawiasie.
export const REPORT_GROUPS: ReportGroup[] = [
  {
    title: 'Sprzedaż',
    icon: IconChartBar,
    reports: [
      {
        code: 'r1',
        to: '/raporty/r1',
        label: 'Raport 1 (sprzedaż zbiorcza)',
        icon: IconChartBar,
      },
      {
        code: 'r2',
        to: '/raporty/r2',
        label: 'Raport 2 (pozycje sprzedażowe)',
        icon: IconChartBar,
      },
    ],
  },
  {
    title: 'CRM',
    icon: IconAddressBook,
    reports: [
      {
        code: 'crm',
        to: '/raporty/aktywnosc-crm',
        label: 'Aktywność CRM',
        icon: IconAddressBook,
      },
    ],
  },
  {
    title: 'Dane',
    icon: IconDatabase,
    reports: [
      // Historia dystrybucji jedzie na uprawnieniu `r2` — to drugi ekran tego
      // samego raportu, nie osobny kod. Grupa jest inna niż uprawnienie i to
      // jest w porządku: szuflada w menu nie ma odbicia w RBAC.
      {
        code: 'r2',
        to: '/raporty/historia-dystrybucji',
        label: 'Historia dystrybucji',
        icon: IconHistory,
      },
    ],
  },
  {
    title: 'Księgowość',
    icon: IconReportAnalytics,
    reports: [
      {
        code: 'rzis',
        to: '/raporty/rzis',
        label: 'Rachunek Zysków i Strat',
        icon: IconReportAnalytics,
      },
    ],
  },
  {
    title: 'Wdrożenia',
    icon: IconClockHour4,
    reports: [
      {
        code: 'opr',
        to: '/raporty/oprogramowanie',
        label: 'Godziny wdrożeniowe',
        icon: IconClockHour4,
      },
    ],
  },
  {
    title: 'Projekty',
    icon: IconFolders,
    reports: [
      {
        code: 'projekty',
        to: '/raporty/podsumowanie-projektow',
        label: 'Podsumowanie',
        icon: IconLayoutDashboard,
      },
      {
        code: 'projekty',
        to: '/raporty/wynik-projektow',
        label: 'Wynik projektów',
        icon: IconReportAnalytics,
      },
      {
        code: 'projekty',
        to: '/raporty/projekty',
        label: 'Godziny i koszt pracy',
        icon: IconClockHour4,
      },
      {
        code: 'projekty',
        to: '/raporty/kontrahenci-projektow',
        label: 'Pracownicy na kontrahentach',
        icon: IconAddressBook,
      },
      {
        code: 'projekty',
        to: '/raporty/projekty-w-czasie',
        label: 'Projekty w czasie',
        icon: IconHistory,
      },
      {
        code: 'projekty',
        to: '/raporty/udzial-w-wyniku',
        label: 'Udział pracowników w wyniku',
        icon: IconChartBar,
      },
    ],
  },
];

/** Grupy przefiltrowane uprawnieniami; puste grupy wypadają, żeby nikt nie
 * patrzył na szufladę bez zawartości. */
export function visibleReportGroups(isAdmin: boolean, allowed: Set<string>): ReportGroup[] {
  return REPORT_GROUPS.map((group) => ({
    ...group,
    reports: group.reports.filter((report) => isAdmin || allowed.has(report.code)),
  })).filter((group) => group.reports.length > 0);
}
